import { FglCommand, FglCommandName, FglNode, FglText } from './parser';
import {
  FglCharBox,
  FglDirection,
  FglElement,
  FglFont,
  FglQrApplyTilde,
  FglQrEncodeMode, FglQrErrorCorrection,
  FglQrPointSize,
} from './types';

type FglCursor = {
  x: number;
  y: number;
  direction: FglDirection;
  heightScale: number; // Optional height scale for text elements
  widthScale: number; // Optional width scale for text elements
  font: FglFont;
  ttf: boolean;
  fontSizePt: number;
  boxSize?: FglCharBox;
  scaleDown: number;
  qrVersion: 2 | 7 | 11 | 15;
};

type FglCommandHandler = (
  node: FglCommand,
  cursor: FglCursor,
  elements: FglElement[],
) => void;

const commandHandlers: Record<FglCommandName, FglCommandHandler> = {
  [FglCommandName.RC]: (node, cursor, _elements) => {
    if (node.command === FglCommandName.RC && node.params.length === 2) {
      cursor.y = node.params[0];
      cursor.x = node.params[1];
    }
  },

  [FglCommandName.BX]: (node, cursor, elements) => {
    if (node.command === FglCommandName.BX && node.params.length === 2) {
      elements.push({
        id: `box-${elements.length}`,
        type: 'box',
        x: cursor.x,
        y: cursor.y,
        width: node.params[1],
        height: node.params[0],
      });
    }
  },

  [FglCommandName.NR]: (node, cursor, _elements) => cursor.direction = 'normal',
  [FglCommandName.RR]: (node, cursor, _elements) => cursor.direction = 'right',
  [FglCommandName.RL]: (node, cursor, _elements) => cursor.direction = 'left',
  [FglCommandName.RU]: (node, cursor, _elements) => cursor.direction = 'upside-down',

  [FglCommandName.HW]: (node, cursor, _elements) => {
    if (node.params.length === 2) {
      cursor.heightScale = node.params[0];
      cursor.widthScale = node.params[1];
    }
  },

  [FglCommandName.F]: (node, cursor, _elements) => {
    if (node.params.length === 1) {
      cursor.font = node.params[0] as FglFont;
      cursor.ttf = false;
      cursor.boxSize = undefined;
    }
  },

  [FglCommandName.TTF]: (node, cursor, _elements) => {
    if (node.params.length === 2) {
      cursor.font = node.params[0] as FglFont;
      cursor.ttf = true;
      cursor.fontSizePt = node.params[1];
      cursor.boxSize = undefined;
    }
  },

  [FglCommandName.RTF]: (node, cursor, _elements) => {
    commandHandlers[FglCommandName.TTF](node, cursor, _elements);
  },

  [FglCommandName.BS]: (_node, _cursor, _elements) => {
    // It seems the printer doesn't actually support BS command.

    // if (node.params.length === 2) {
    //   cursor.boxSize = {
    //     w: node.params[1],
    //     h: node.params[0],
    //   };
    // } else {
    //   cursor.boxSize = undefined; // Reset box size if not provided
    // }
  },

  [FglCommandName.SD]: (node, cursor, _elements) => {
    if (node.params.length === 1) {
      cursor.scaleDown = node.params[0];
    }
  },

  [FglCommandName.QRV]: (node, cursor, _elements) => {
    if (node.params.length === 1) {
      cursor.qrVersion = node.params[0] as 2 | 7 | 11 | 15;
    }
  },

  [FglCommandName.QR]: (node, cursor, elements) => {
    const text = node.qrText || '';

    const qrPointSize: FglQrPointSize = <FglQrPointSize>node.params[0] || 3;
    const qrApplyTilde: FglQrApplyTilde = <FglQrApplyTilde>node.params[1] || 0;
    let qrEncodeMode: FglQrEncodeMode;
    switch (node.params[2]) {
      case 1:
        qrEncodeMode = 'alphanumeric';
        break;
      case 2:
        qrEncodeMode = 'numeric';
        break;
      default:
        qrEncodeMode = 'byte';
    }
    let qrErrorCorrection: FglQrErrorCorrection;
    switch (node.params[3]) {
      case 1:
        qrErrorCorrection = 'L';
        break;
      case 2:
        qrErrorCorrection = 'H';
        break;
      case 3:
        qrErrorCorrection = 'Q';
        break;
      default:
        qrErrorCorrection = 'M';
    }

    elements.push({
      id: `qr-${elements.length}`,
      type: 'qr',
      x: cursor.x,
      y: cursor.y,
      version: cursor.qrVersion,
      pointSize: qrPointSize,
      applyTilde: qrApplyTilde,
      encodeMode: qrEncodeMode,
      errorCorrection: qrErrorCorrection,
      text: text,
    });
  },
};

const handleCommand: FglCommandHandler = (
  node: FglCommand,
  cursor: FglCursor,
  elements: FglElement[],
) => {
  const handler = commandHandlers[node.command];
  if (handler) {
    handler(node, cursor, elements);
  }
};

const handleText = (
  node: FglText,
  cursor: FglCursor,
  elements: FglElement[],
) => {
  elements.push({
    id: `text-${elements.length}`,
    type: 'text',
    x: cursor.x,
    y: cursor.y,
    text: node.text,
    direction: cursor.direction,
    font: cursor.font,
    ttf: cursor.ttf,
    fontSizeTTF: cursor.fontSizePt,
    scaleX: cursor.widthScale / cursor.scaleDown,
    scaleY: cursor.heightScale / cursor.scaleDown,
    boxSize: cursor.boxSize,
  });
};

export const astToElements = (ast: FglNode[]): FglElement[] => {
  const elements: FglElement[] = [];
  const cursor: FglCursor = {
    x: 0,
    y: 0,
    direction: 'normal',
    heightScale: 1, // Default height scale for text elements
    widthScale: 1, // Default width scale for text elements
    font: 3,
    fontSizePt: 12,
    ttf: false,
    scaleDown: 1,
    qrVersion: 7,
  };

  for (const node of ast) {
    if (node.type === 'command') {
      handleCommand(node, cursor, elements);
    } else if (node.type === 'text') {
      handleText(node, cursor, elements);
    }
  }

  return elements;
};
